
import { ViewContext, ActionButtonType } from "@/components/movie/buttons/types";
import { ButtonProps } from "@/components/ui/button";

// Define valid button variant types for strict type checking
export type ActionButtonVariant = 
  | "gridButton" 
  | "gridButtonSelected" 
  | "gridButtonUndo"
  | "listButton" 
  | "listButtonSelected" 
  | "listButtonUndo"
  | "searchButton" 
  | "searchButtonSelected"
  | "detailButton" 
  | "detailButtonSelected" 
  | "detailButtonUndo";

// Define the size type to match Button component's size prop
export type ActionButtonSize = ButtonProps['size'];

/**
 * Simplified hook to manage action button styling logic
 * Only returns the variant, size, and label text
 */
export function useActionButtonStyles(
  intent: ActionButtonType,
  context: ViewContext,
  isActive: boolean,
  isDeselected: boolean = false
) {
  // Determine the correct button variant to use based on context and state
  const getVariantKey = (): ActionButtonVariant => {
    // Fixed logic: isDeselected should use the base variant, not the selected variant
    // Only isActive (without deselected) should determine the selected visual state
    const visuallySelected = isActive && !isDeselected;
    
    // Grid view context - use gridButton variants
    if (context === "grid") {
      return visuallySelected ? "gridButtonSelected" : "gridButton";
    }
    
    // List view context - use listButton variants
    if (context === "list") {
      return visuallySelected ? "listButtonSelected" : "listButton";
    }
    
    // Search results context - use searchButton variants
    if (context === "search") {
      return visuallySelected ? "searchButtonSelected" : "searchButton";
    }
    
    // Dialog or detail context - use detailButton variants
    if (context === "dialog" || context === "detail") {
      return visuallySelected ? "detailButtonSelected" : "detailButton";
    }
    
    // Default fallback for any other context
    return "gridButton";
  };
  
  // Get the button variant key
  const variant = getVariantKey();
  
  // Get size prop based on variant - explicitly typed as ActionButtonSize
  const size: ActionButtonSize = context === "dialog" || context === "detail" ? "default" : "badge";
  
  // Get label text based on intent
  const labelText = intent === "favorite" ? "Gem" : "Watchlist";
  
  return {
    variant,
    size,
    labelText
  };
}
